import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CASE_STUDIES, IMAGES, formatINR } from "@/data/content";
import api, { openWhatsApp } from "@/lib/api";
import { SectionEyebrow } from "@/components/Marquee";

export default function CaseStudies() {
  const [cases, setCases] = useState(CASE_STUDIES);

  useEffect(() => {
    api.get("/case-studies")
      .then((res) => {
        const fetched = res.data?.case_studies || [];
        if (fetched.length > 0) setCases(fetched);
      })
      .catch(() => {
        // keep the static fallback on any error
      });
  }, []);

  const flagship = cases.find((c) => c.car.includes("Fortuner")) || cases[0];
  return (
    <section id="cases" className="relative py-20 md:py-28 bg-[#0A0A0F]" data-testid="cases-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <SectionEyebrow n="Ch. VI" testId="cases-eyebrow">Proof of Savings</SectionEyebrow>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.8, 0, 0.2, 1] }}
          className="mt-6 font-serif text-white text-5xl sm:text-6xl md:text-7xl leading-[0.98] tracking-tight max-w-4xl"
        >
          Real deals. Real buyers. <span className="italic gradient-text">Real savings.</span>
        </motion.h2>
        <p className="mt-4 text-xs text-silver/45 max-w-2xl">
          ₹40K average verified savings, based on completed negotiations calculated against each deal's signed benchmark price.
        </p>

        {flagship && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
            className="mt-14 rounded-3xl gradient-border bg-[#141323] p-8 md:p-14 grid md:grid-cols-12 gap-8 items-center"
            data-testid="flagship-case-study"
          >
            <div className="md:col-span-5">
              <p className="eyebrow gradient-text">Saved by one buyer</p>
              <p className="mt-3 font-serif text-white leading-none text-[16vw] md:text-7xl">{formatINR(flagship.saved)}</p>
              <p className="mt-4 text-silver/70 text-sm">{flagship.car} · {flagship.city}</p>
            </div>
            <div className="md:col-span-4 flex md:block gap-8">
              <div>
                <p className="text-xs text-silver/50 uppercase tracking-wider">Dealer quote</p>
                <p className="mt-1 font-serif text-2xl text-silver/60 line-through">{formatINR(flagship.dealer)}</p>
              </div>
              <div className="mt-0 md:mt-5">
                <p className="text-xs text-silver/50 uppercase tracking-wider">Final negotiated</p>
                <p className="mt-1 font-serif text-2xl text-white">{formatINR(flagship.final)}</p>
              </div>
            </div>
            <div className="md:col-span-3">
              <p className="font-serif italic text-xl md:text-2xl text-white/90 leading-snug">
                Same car. Same buyer. <span className="gradient-text">Different negotiator.</span>
              </p>
            </div>
          </motion.div>
        )}

        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {cases.map((c, i) => (
            <motion.article
              key={c.id || c.car}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.08 }}
              className={`relative rounded-3xl overflow-hidden border ${i === 0 ? "gradient-border bg-[#141323]" : "border-white/5 bg-surface"} p-8 md:p-10`}
              data-testid={`case-card-${i}`}
            >
              {i === 0 && (
                <div className="absolute top-5 right-5 text-[10px] uppercase tracking-[0.25em] px-3 py-1 rounded-full gradient-bg text-white">
                  Featured
                </div>
              )}
              <p className="eyebrow gradient-text">{c.city}</p>
              <h3 className="mt-2 font-serif text-2xl md:text-3xl text-white leading-tight">{c.car}</h3>
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-silver/50 uppercase tracking-wider">Dealer's quote</p>
                  <p className="mt-1 font-serif text-xl text-silver/60 line-through">{formatINR(c.dealer)}</p>
                </div>
                <div>
                  <p className="text-xs text-silver/50 uppercase tracking-wider">Final price</p>
                  <p className="mt-1 font-serif text-xl text-white">{formatINR(c.final)}</p>
                </div>
              </div>
              <div className="mt-6 py-4 border-t border-b border-white/5">
                <p className="text-xs uppercase tracking-wider gradient-text">You saved</p>
                <p className="mt-1 font-serif text-4xl text-white">{formatINR(c.saved)}</p>
              </div>
              {c.breakdown && (
                <div className="mt-5">
                  <p className="text-[11px] uppercase tracking-widest text-silver/45">How we got there</p>
                  <ul className="mt-3 space-y-1.5">
                    {c.breakdown.map((b) => (
                      <li key={b.label} className="flex items-center justify-between text-xs text-silver/70">
                        <span>{b.label}</span>
                        <span className="text-white/80">{b.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="mt-6 italic font-serif text-lg text-silver/85 leading-snug">"{c.quote}"</p>
              <p className="mt-3 text-xs text-silver/50">— {c.buyer}</p>
              <button
                onClick={() => openWhatsApp({ source: "case_study", context: c.car })}
                className="mt-5 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-silver/60 hover:text-white transition-colors"
                data-testid={`case-cta-${i}`}
              >
                See the case study →
              </button>
            </motion.article>
          ))}
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-4">
          <div className="relative rounded-3xl overflow-hidden h-64 md:h-80">
            <img src={IMAGES.case1} alt="Happy customer receiving keys" className="absolute inset-0 h-full w-full object-cover grayscale-[30%]" />
            <div className="absolute inset-0 bg-gradient-to-tr from-void via-void/40 to-transparent" />
          </div>
          <div className="relative rounded-3xl overflow-hidden h-64 md:h-80">
            <img src={IMAGES.case2} alt="Handshake at delivery" className="absolute inset-0 h-full w-full object-cover grayscale-[30%]" />
            <div className="absolute inset-0 bg-gradient-to-tl from-void via-void/40 to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
}
